import styled, { css } from 'styled-components'
import {
    svgStroke, flashKeyframes,
    belowTablet, belowPhone, fromDesktop, fromPhone
} from 'utilities/styled'
import { Grid } from 'components/grid'
import Caption, { CaptionSmall } from 'components/typography/caption'
import FileNameIconBase from 'components/file-name-icon'
import { Columns } from 'components/flex'
import Link from 'components/link'
import { Units as UnitsBase } from 'components/units'
import SigningStatusBase from 'pages/documents/signing-status'

export const Document = styled(Grid)`
    position: relative;

    grid-template-rows:
        [main-start date] 24px
        [creator] minmax(0, 1fr) [main-end];
    grid-template-columns: subgrid;
    grid-column: wrap;
    column-gap: inherit;
    align-items: start;

    min-height: 72px;
    padding-block: 16px;

    &::after {
        content: "";
        position: absolute;
        top: 0;
        inset-inline: 0;

        height: 1px;
        background: var(--huma-color-border-default);

        ${fromDesktop`
            inset-inline: 48px 0;
        `}
    }

    ${belowTablet`
        .has-selected :nth-child(1 of &) {
            padding-block-start: 0;

            &::after {
                display: none;
            }
        }
    `}

    ${belowPhone`
        grid-template-rows: unset;
        grid-template-columns:
            [bulk] 24px
            [name editable1 editable2] minmax(0, 1fr)
            [actions] 40px;
        row-gap: 4px;
    `}

    ${fromPhone`
        &.signature {
            grid-template-rows:
                [main-start date] 24px
                [creator signature] minmax(0, 1fr) [main-end];

            &.concerns {
                grid-template-rows:
                    [main-start date] 24px
                    [creator date] minmax(0, 1fr)
                    [signature main-end] min-content;
            }
        `}
    }

    &.slim {
        grid-template-columns: 1fr;
        grid-column: unset;
    }

    &.flash {
        animation: ${flashKeyframes} 2s ease-in-out;
    }
`

export const BulkCell = styled.div`
    grid-area: main / bulk;

    ${belowPhone`
        grid-row: 1;
    `}

    & label {
        opacity: calc(1 / 3);
        transform-origin: center;
        transition: all .2s ease-in-out;

        ${fromPhone`
            scale: .75;
        `}
    }

    ${Document}:hover & label,
    ${Document} & input:focus-visible + label {
        opacity: calc(2 / 3);

        ${fromPhone`
            scale: .875;
        `}
    }

    &:hover label,
    & input:focus-visible + label,
    ${Document}.selected & label {
        opacity: 1 !important;
        scale: 1 !important;
    }
`

export const NameCell = styled(Columns)`
    grid-area: main / name;
    flex-direction: column;
    align-items: flex-start;

    max-width: 100%;

    ${belowPhone`
        grid-row: 1;
    `}

    ${Document}.slim & {
        grid-area: unset;

        min-width: 0;
    }

    svg.feather {
        ${svgStroke()}
    }
`

export const FileNameIcon = styled(FileNameIconBase)`
    align-items: center;
`

export const CreatedCell = styled(Columns)`
    display: grid;
    grid-row: main;
    grid-column: editable2;
    grid-template-rows: subgrid;

    line-height: 24px;

    ${NameCell} + & {
        grid-column: editable1;
    }

    ${belowPhone`
        display: none;
    `}

    ${fromPhone`
        flex-direction: column;
        align-items: flex-start;

        > span {
            grid-row: date;
        }

        > p {
            grid-row: creator;
        }
    `}
`

export const AccessCell = styled(Columns)`
    grid-area: main / editable2;
    align-items: flex-start;

    ${NameCell} + & {
        grid-column: editable1;
    }

    ${belowPhone`
        grid-row: concerns;
        display: inline;
    `}
`

export const CategoryCell = styled(AccessCell)`
    grid-column: editable2;

    ${NameCell} + & {
        grid-column: editable1;
    }
`

export const ActionsCell = styled(Columns)`
    grid-row: main;
    grid-column: actions;

    ${belowPhone`
        grid-row: 1 / span 2;
    `}

    .actions-button {
        margin-block: -8px;
    }
`

export const EditLink = styled(Link)`
    display: block;
    max-width: 100%;

    &:is(:hover, :focus-within) {
        text-decoration: none;
    }

    svg {
        margin-inline-end: 24px;

        ${belowPhone`
            margin-inline-end: 16px;
        `}
    }
`

const summaryCss = css`
    ${fromPhone`
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    `}

    &:not(:last-child) {
        margin: 0 0 16px;
    }
`

export const ConcernsSummary = styled(CaptionSmall)`
    ${summaryCss}

    margin-inline-start: 48px;

    ${belowPhone`
        margin-block-end: 8px;
        margin-inline-start: 40px;
    `}
`

export const Units = styled(UnitsBase)`
    max-width: 100%;

    &.blob {
        padding-block: unset;
    }
`

export const AccessSummary = styled(Caption)`
    ${summaryCss}

    ${belowPhone`
        margin-inline-start: 40px;

        font-size: 14px;
        line-height: 20px;
    `}
`

export const Category = AccessSummary

export const ConcernsColumns = styled(Columns)`
    align-items: center;
    height: 100%;
    color: var(--huma-color-foreground-default);
    text-decoration: none;
`

export const SigningStatus = styled(SigningStatusBase)`
    grid-area: signature / name;

    margin-inline-start: 48px;

    ${belowPhone`
        grid-row: 3;

        margin-inline-start: 40px;

        p {
            color: var(--huma-color-foreground-subtle);
        }
    `}

    ${fromPhone`
        ${Document}.concerns & {
            margin-block-start: 4px;
        }
    `}
`

export const MediaQuery = styled.div`
    ${belowPhone`
        &.from-phone {
            display: none;
        }
    `}

    ${fromPhone`
        &.below-phone {
            display: none;
        }
    `}
`